import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppSidebar } from "@/components/app-sidebar";
import { ChallengeList } from "@/components/challenge-list";
import { ChallengeLeaderboard } from "@/components/challenge-leaderboard";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getChallenges, Challenge } from "@/services/challengeService";
import { NewChallengeDialog } from "@/components/new-challenge-dialog";

export default function ChallengesPage() {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = currentUser?.role === "admin";
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: challenges = [], isLoading } = useQuery({
    queryKey: ['challenges'],
    queryFn: getChallenges,
  });

  useEffect(() => {
    if (challenges.length > 0 && !selectedChallenge) {
      setSelectedChallenge(challenges[0]);
    }
  }, [challenges, selectedChallenge]);

  const handleChallengeSelect = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['challenges'] });
  };

  const handleChallengeUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['challenges'] }).then(() => {
      if (selectedChallenge) {
        const updatedChallenge = challenges.find(c => c.id === selectedChallenge.id);
        if (updatedChallenge) {
          setSelectedChallenge(updatedChallenge);
        }
      }
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">Vertriebschallenges</h1>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Button onClick={() => setIsDialogOpen(true)}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Neue Challenge
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4">
              <ChallengeList 
                challenges={challenges} 
                onSelect={handleChallengeSelect} 
                selectedId={selectedChallenge?.id}
                isLoading={isLoading}
                isAdmin={isAdmin}
                onChallengeUpdate={handleChallengeUpdate}
              />
            </div>
            <div className="lg:col-span-8">
              {selectedChallenge ? (
                <ChallengeLeaderboard challenge={selectedChallenge} />
              ) : (
                <div className="bg-muted/40 rounded-lg p-8 text-center h-[400px] flex flex-col items-center justify-center">
                  <h3 className="text-xl font-medium mb-2">Wähle eine Challenge</h3>
                  <p className="text-muted-foreground">
                    Bitte wähle eine Challenge aus der Liste, um die Rangliste anzusehen.
                  </p>
                </div>
              )}
            </div>
          </div>

          {isAdmin && isDialogOpen && (
            <NewChallengeDialog open={isDialogOpen} onClose={handleDialogClose} />
          )}
        </div>
      </div>
    </div>
  );
}
